import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';

interface User {
  id: string;
  teamName: string;
  firstName?: string;
  lastName?: string;
}

interface Season {
  id: string;
  name: string;
}

interface League {
  id: string;
  name: string;
  description: string;
  creator: User;
  season: Season;
  type: 'fanleague';
  isActive: boolean;
  maxMembers: number;
  currentMembers: number;
  groupname: string;
  createdAt: string;
}

const FanLeague: React.FC = () => {
  const { groupname } = useParams<{ groupname: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [league, setLeague] = useState<League | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [isAlreadyMember, setIsAlreadyMember] = useState(false);

  useEffect(() => {
    const fetchFanLeague = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/leagues/by-groupname/${groupname}`);
        setLeague(response.data);
        
        // Store the groupname in sessionStorage for auto-join on registration
        sessionStorage.setItem('fanleague_groupname', groupname || '');
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError('Fan league not found');
        } else {
          setError(err.response?.data?.message || 'Failed to load fan league');
        }
      } finally {
        setLoading(false);
      }
    };

    if (groupname) {
      fetchFanLeague();
    }
  }, [groupname]);

  // Check membership when user or league changes
  useEffect(() => {
    const checkMembership = async () => {
      if (user && league) {
        try {
          const userLeaguesResponse = await apiClient.get('/leagues/my');
          const userLeagues = userLeaguesResponse.data || [];
          const isMember = userLeagues.some((membership: any) => 
            membership.league?.id === league.id
          );
          setIsAlreadyMember(isMember);
        } catch (membershipError) {
          console.error('Failed to check membership:', membershipError);
          setIsAlreadyMember(false);
        }
      } else {
        setIsAlreadyMember(false);
      }
    };

    checkMembership();
  }, [user, league]);

  const handleJoinLeague = async () => {
    if (!league || !user) return;
    
    setJoining(true);
    try {
      await apiClient.post(`/leagues/join-fanleague/${league.groupname}`);
      
      // Clear the league cache so the joined league appears immediately in the leagues list
      sessionStorage.removeItem('leagues_data');
      sessionStorage.removeItem('user_leagues_data');
      sessionStorage.removeItem('leagues_cache_timestamp');
      
      // Update membership state
      setIsAlreadyMember(true);
      // Redirect to league detail page after successful join
      navigate(`/leagues/${league.id}`);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to join league');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading fan league...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">{error}</div>
          <Link to="/" className="btn-primary">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Fan league not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative text-white py-16">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/files/images/worldcup-flags.png')" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-black/60" aria-hidden />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            {league.name}
          </h1>
          <p className="text-xl md:text-2xl opacity-90 mb-8">
            Join our fan league and compete with friends and family or across the world!
          </p>


          <div className="bg-white/25 backdrop-blur-sm rounded-lg p-6 inline-block">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold">{league.season.name}</div>
                <div className="text-sm opacity-80">Season</div>
              </div>
              <div>


              {/* Call to Action */}
              <div className="text-center">
                {user ? (
                  <div className="flex justify-center">
                    {isAlreadyMember ? (
                      <>
                        <button
                          onClick={() => navigate(`/leagues/${league.id}`)}
                          className="btn-primary text-sm py-4 px-8 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                        >
                          View League
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={handleJoinLeague}
                          disabled={joining}
                          className="btn-primary text-sm py-4 px-8 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50"
                        >
                          {joining ? 'Joining...' : `Join ${league.name}`}
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-6 justify-center items-center">
                      <Link
                        to="/register"
                        className="btn-primary text-sm py-4 px-8 w-full md:w-auto shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                      >
                        Register & Join League
                      </Link>
                      <Link
                        to="/login"
                        className="btn-trans text-sm py-4 px-8 w-full md:w-auto shadow-lg transform hover:scale-105 transition-all duration-300"
                        onClick={() => sessionStorage.setItem('fanleague_groupname', groupname || '')}
                      >
                        Already Have Account? Sign In
                      </Link>
                    </div>
                  </div>
                )}
              </div>



              </div>
            </div>
          </div>
        </div>
      </div>



      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Description */}
        {league.description && (
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              About This League
            </h2>
            <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
              {league.description}
            </p>
          </div>
        )}

        {/* How It Works */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="text-center p-6 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Join the League
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Register and join our fan league and start making predictions
            </p>
          </div>
          
          <div className="text-center p-6 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Make Predictions
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Predict match scores and results to earn points
            </p>
          </div>
          
          <div className="text-center p-6 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Compete & Win
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Climb the leaderboard and fight for the Predictor championship
            </p>
          </div>
        </div>



        {/* Call to Action */}
        <div className="text-center mt-20 mb-20">
          {user ? (
            <div>
              {isAlreadyMember ? (
                <>
                  <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                    You're Already a Member!
                  </h2>
                  <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                    Welcome back, {user.teamName}! You're already part of this fan league.
                  </p>
                  <button
                    onClick={() => navigate(`/leagues/${league.id}`)}
                    className="btn-primary text-lg py-4 px-8 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                  >
                    View League & Make Predictions
                  </button>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                    Ready to Join?
                  </h2>
                  <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                    Welcome back, {user.teamName}! Join this fan league and start competing.
                  </p>
                  <button
                    onClick={handleJoinLeague}
                    disabled={joining}
                    className="btn-primary text-lg py-4 px-8 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50"
                  >
                    {joining ? 'Joining...' : `Join ${league.name}`}
                  </button>
                </>
              )}
            </div>
          ) : (
            <div>
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Join the Action!
              </h2>
              <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                Register now to join this exclusive fan league and start making predictions.
              </p>
              <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-6 justify-center items-center">
                <Link
                  to="/register"
                  className="btn-primary text-lg py-4 px-8 w-full md:w-auto shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  Register & Join League
                </Link>
                <Link
                  to="/login"
                  className="btn-secondary text-lg py-4 px-8 w-full md:w-auto shadow-lg transform hover:scale-105 transition-all duration-300"
                >
                  Already Have Account? Sign In
                </Link>
              </div>
            </div>
          )}
        </div>



      </div>
    </div>
  );
};

export default FanLeague; 